
import * as React from 'react';
import { IRenderProps } from '../../utilities/components/RenderProps';
import { IActionStateProps, WithAction, evaluateAction } from '../withAction/WithAction';
import { withDisplayName } from '../../utilities/components/Decorators';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';
import { invokeActionControl } from '../../controls/invokeActionControl';
import { IItemCache } from '../../models/IItemCacheObserver';

/**
 * @public
 */
export interface IWithInvokeActionProps extends IRenderProps<IActionStateProps> {
    itemKey: string;
}

/**
 * @beta
 */
export function evaluateInvokeAction(itemCache: IItemCache, props: {
    itemKey: string;
}): JSX.Element | null {
    const controlHandler = itemCache.demandItemFacet(invokeActionControl, undefined);

    const {
        action
    } = invokeActionControl.evaluate(controlHandler)(itemCache, {
        itemKey: props.itemKey
    });

    return evaluateAction(itemCache, action || null);
}

/**
 * Component which renders the action used to invoke a given item, within the context
 * of a list component which uses SelectionZone.
 *
 * SelectionZone bubbles up all 'invocation' UX gestures to a centralized 'onItemInvoked'
 * callback. InvocationAction connects the to the context provided by the connectToInvocationAction
 * to intercept the 'onItemInvoked' callback for a given item and execute the correct action.
 * @public
 */
export const WithInvokeAction: React.ComponentType<IWithInvokeActionProps> =
    withDisplayName('WithInvokeAction')((props: IWithInvokeActionProps) => {
        return (
            <WithItemCache>
                { ({ itemCache }: IItemCacheStateProps) => {
                    return (
                        <WithAction
                            action={ evaluateInvokeAction(itemCache, props) }
                        >
                            { props.children }
                        </WithAction>
                    );
                } }
            </WithItemCache>
        );
    });
